import {isEqual} from "lodash";
import {inject, injectable} from "inversify";
import type {ChangeEvent, SyntheticEvent} from "react";
import {action, makeAutoObservable, observable} from "mobx";
import {AxiosError} from "axios";
import {RequestState} from "data/enums";
import {extractErrorMessage} from "data/utils";
import {Bindings} from "data/constants/bindings";
import type {IApiResponse} from "data/services/http";
import {ViewController} from "data/types/structure";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {IUpdateUserPayload} from "data/providers/api/user.api.provider";

export interface ICommunicationAccountController extends ViewController {
	readonly i18n: ILocalizationStore;
	get isButtonDisabled(): boolean;
	get error(): Record<string, string>;
	get state(): Record<string, boolean>;

	handleFormSubmit: (e: SyntheticEvent) => void;
	handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class CommunicationAccountController implements ICommunicationAccountController {
	@observable private _requestState = RequestState.IDLE;
	@observable private _errorMsg: Record<string, string> = {};
	@observable private _formState = {
		isNotificationsEnabled: false,
	};

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore
	) {
		makeAutoObservable(this);

		const user = this._userStore.user;

		if (user) {
			this._formState = {
				isNotificationsEnabled: user.isNotificationsEnabled,
			};
		}
	}

	get state() {
		return this._formState;
	}

	get isButtonDisabled() {
		return isEqual(this._requestState, RequestState.PENDING) || !this.formHasChanges;
	}

	get error() {
		return this._errorMsg;
	}

	private get formHasChanges() {
		const user = this._userStore.user;

		const newData = {
			isNotificationsEnabled: this._formState.isNotificationsEnabled,
		};

		const data = {
			isNotificationsEnabled: user?.isNotificationsEnabled,
		};

		return !isEqual(data, newData);
	}

	public handleFormSubmit = (event: SyntheticEvent) => {
		event.preventDefault();

		const user = this._userStore.user;

		if (!user) {
			return;
		}

		const payload: IUpdateUserPayload = {
			displayName: user.displayName,
			email: user.email,
			isNotificationsEnabled: this._formState.isNotificationsEnabled,
			isOptIn: user.isOptIn,
		};

		this._requestState = RequestState.PENDING;

		this._userStore.update(payload).then(this.onSuccess).catch(this.onError);
	};

	@action private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	@action private onError = (error: AxiosError<IApiResponse<unknown>>) => {
		this._errorMsg = {common: extractErrorMessage(error)};
		this._requestState = RequestState.ERROR;
	};

	@action handleClearErrorOnChange = () => {
		this._errorMsg = {};
	};

	handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		this._formState = {
			...this._formState,
			[e.target.name]: e.target.checked,
		};

		this.handleClearErrorOnChange();
	};
}
